import { useEffect, useMemo, useState } from 'react';
import {
  useGetSendCandidatesQuery,
  useLazyGetSendCandidatesQuery,
} from 'store/services/tradeOffers/tradeOffersApi';
import { OfferCandidateWantGroup } from 'types/interfaces/tradeOffer';

/**
 * Ownership index for the full-want-list mode: every intersection match keyed
 * by want-entry id. Built from the WHOLE intersection (no search, no paging),
 * so searched or paged full-list rows still get their owned marks. The
 * limit-1 count query shares its cache entry with SourcePanel's match-count
 * query, so enabling the index adds at most one request in practice.
 */
export const useSendMatchIndex = (userId: number, enabled: boolean) => {
  const countQuery = useGetSendCandidatesQuery(
    { userId, mode: 'intersection', page: 1, limit: 1 },
    { skip: !enabled }
  );
  const totalCount = countQuery.data?.totalCount;

  const mainQuery = useGetSendCandidatesQuery(
    { userId, mode: 'intersection', page: 1, limit: Math.max(totalCount ?? 0, 1) },
    { skip: !enabled || totalCount == null }
  );

  // Defensive paging: if the backend caps limit below the intersection size,
  // walk the remaining pages — an incomplete index would silently render
  // owned rows as unowned.
  const [extraGroups, setExtraGroups] = useState<OfferCandidateWantGroup[]>([]);
  const [extraDone, setExtraDone] = useState(false);
  const [fetchPage] = useLazyGetSendCandidatesQuery();

  useEffect(() => {
    let cancelled = false;
    const data = mainQuery.data;
    setExtraGroups([]);
    setExtraDone(!data?.hasMore);
    if (!enabled || !data || !data.hasMore) return undefined;
    const limit = Math.max(totalCount ?? 0, 1);
    (async () => {
      const acc: OfferCandidateWantGroup[] = [];
      let page = data.page + 1;
      let hasMore = true;
      while (hasMore && !cancelled) {
        const res = await fetchPage(
          { userId, mode: 'intersection', page, limit },
          true
        ).unwrap();
        acc.push(...res.candidates);
        hasMore = res.hasMore;
        page += 1;
      }
      if (!cancelled) {
        setExtraGroups(acc);
        setExtraDone(true);
      }
    })().catch(() => {
      // A failed tail fetch leaves the index incomplete; keep it not-ready so
      // the panel stays in its loading state rather than mislabeling rows.
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, mainQuery.data, userId, totalCount, fetchPage]);

  // Whole groups, not just rows: the condition selector needs the want
  // entry's acceptedConditions alongside its matches.
  const index = useMemo(() => {
    const map = new Map<number, OfferCandidateWantGroup>();
    if (!mainQuery.data) return map;
    for (const group of [...mainQuery.data.candidates, ...extraGroups]) {
      if (group.matches.length > 0) map.set(group.wantEntry.id, group);
    }
    return map;
  }, [mainQuery.data, extraGroups]);

  const isReady = enabled && !!mainQuery.data && extraDone;

  return {
    index,
    isReady,
    isLoading: enabled && !isReady,
  };
};
